import React, { ReactElement, useEffect, useState } from 'react';
import { MenuProps } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { ExtendedTable } from 'common/components/ExtendedTable/ExtendedTable';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'common/constants/table.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { removeEmpty } from 'common/utils/object.utils';
import { useGetCounterpartiesQuery } from 'domains/counterparties/queries/counterparty.query';
import { TCounterpartiesListReqParams } from 'domains/counterparties/types/counterparty.types';
import { useDeletePermissionMutation, useGetPermissionsListQuery } from 'domains/permissions/queries/permissions.query';
import {
  TGetPermissionsListResData,
  TPermissionsReqParams
} from 'domains/permissions/types/permissions.types';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import {
  FiltersFormComponent
} from './components/filters-form/filters-form.component';
import {
  EPermissionAction,
  EPermissionsTableColumnKey,
  EModalType,
  EUserManagementPermissionsPageGetParams,
  EUserManagementPermissionsPageGetParamsActionTypes,
  EPermissionsFilterFormKeys,
} from './enums/permissions.enums';
import CreateEditPermissionsModal from './modals/create-edit-permissions.modal';
import { SDangerActionItem } from './permissions.page.style';
import { getColumns } from './table/permissions-table.columns';
import {
  TPermissionModalData,
  TPermissionsTableRecord,
  TTablePaginationConfig,
} from './types/permissions.types';

const UserRoleManagementPermissionsPage = (): ReactElement => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [modalData, setModalData] = useState<TPermissionModalData | {}>({});
  const [paginationData, setPaginationData] = useState<TTablePaginationConfig>({
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [form] = useForm<EPermissionsFilterFormKeys>();
  const [_filterValues, setFilterValues] = useState<TPermissionsReqParams>({});
  const filterValues = removeEmpty(_filterValues);
  const [submitFilter, setSubmitFilter] = useState<boolean>(false);

  const {
    data: counterpartiesData,
    isError: isCounterpartiesDataError,
    error: counterpartiesDataError,
  } = useGetCounterpartiesQuery(
    {},
    {
      refetchOnMount: false,
    },
  );

  const {
    data: permissionsListData,
    isLoading: isPermissionsListDataLoading,
    isError: isPermissionsListDataError,
    error: permissionsListDataError,
  } = useGetPermissionsListQuery(
    {
      limit: paginationData.pageSize,
      offset: (paginationData.page - 1) * paginationData.pageSize,
      ...filterValues,
    },
    {
      refetchOnMount: false,
    },
  );

  const isError = isPermissionsListDataError || isCounterpartiesDataError;
  const error = permissionsListDataError || counterpartiesDataError;

  useEffect(() => {
    if (isError) {
      console.error(error);
    }
  }, [isError, error]);

  useEffect(() => {
    const searchParams = new URLSearchParams(document.location.search);
    const pageAction = searchParams.get(EUserManagementPermissionsPageGetParams.Action);

    if (pageAction === EUserManagementPermissionsPageGetParamsActionTypes.Create) {
      setModalData({
        type: EModalType.Create,
        rowData: {},
        counterpartiesData,
      });
    }
  }, [document.location.search, counterpartiesData]);

  const {mutateAsync: deletePermissionMutation} = useDeletePermissionMutation();

  const actions: MenuProps['items'] = [
    {
      key: EPermissionAction.Edit,
      label: t('usrRoleMng.permissions.table.rowAction.edit'),
    },
    {
      key: EPermissionAction.Delete,
      label: <SDangerActionItem>{t('usrRoleMng.permissions.table.rowAction.delete')}</SDangerActionItem>,
    },
  ];

  const onItemActionClick = async (
    {record, actionKey}: { record: TPermissionsTableRecord; actionKey: string }
  ): Promise<void> => {
    try {
      switch (actionKey) {
        case EPermissionAction.Edit: {
          setModalData({
            type: EModalType.Edit,
            rowData: record,
            counterpartiesData,
          });
          break;
        }
        case EPermissionAction.Delete: {
          await deletePermissionMutation({permissionGUID: record.guid});
          await queryClient.invalidateQueries({
            queryKey: queryKeysClient[EQueryKeys.PermissionQueryKeys].getPermissionsList._def,
          });
          break;
        }
        default: {
          break;
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handlePaginationChange = (pageParam: number, pageSizeParam: number): void => {
    setPaginationData({
      page: pageParam,
      pageSize: pageSizeParam,
    });
  };

  const columns = getColumns({
    menuItems: actions,
    onItemActionClick,
  });

  const onCancelModalHandler = (): void => {
    setModalData({});
    navigate(ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_PERMISSIONS);
  };

  const permisssiomTableData: TPermissionsTableRecord[] | undefined = permissionsListData?.map(
    (item): TPermissionsTableRecord => ({
      ...item,
      status: (item?.deleted_at && item?.deleted_at === null) ? 1 : 0,
    }),
  );

  const onSubmit = (values: Partial<Record<EPermissionsFilterFormKeys, string>>): void => {
    const valuesFromForm = {
      ...values,
    } as TPermissionsReqParams;

    const valuesToSend = removeEmpty(valuesFromForm);

    setPaginationData((currentPaginationData) => ({
      page: DEFAULT_PAGE,
      pageSize: currentPaginationData.pageSize,
    }));

    setFilterValues(valuesToSend);
    setSubmitFilter(true);
  };

  useEffect(() => {
    setSubmitFilter(false);
  }, [filterValues]);

  const paginationConfig = {
    style: {
      marginRight: 30,
    },
    showSizeChanger: true,
    showQuickJumper: true,
    total: permissionsListData?.length || 0,
    pageSize: paginationData.pageSize,
    current: paginationData.page,
    onChange: handlePaginationChange,
  };

  return (
    <>
      <ExtendedTable
        pagination={paginationConfig}
        loading={isPermissionsListDataLoading}
        rowKey={EPermissionsTableColumnKey.GUID}
        dataSource={permisssiomTableData}
        columns={columns}
        hideColumnsBtn
        hideFiltersBtn={false}
        submitFilter={submitFilter}
        filtersElem={
          <FiltersFormComponent
            onSubmit={onSubmit}
            form={form}
            isLoading={isPermissionsListDataLoading}
          />
        }
      />
      <CreateEditPermissionsModal
        isOpen={!!Object.keys(modalData).length}
        onCancelModalHandler={onCancelModalHandler}
        data={modalData as TPermissionModalData}
      />
    </>
  );
};

export const loader = async (): Promise<{
  counterpartiesData: TCounterpartiesListReqParams | {},
  permissionsList: TGetPermissionsListResData | {},
}> => {
  try {
    const counterpartiesDataQuery = queryKeysClient[EQueryKeys.CounterpartyQueryKeys].getCounterpartiesList();
    const counterpartiesData: TCounterpartiesListReqParams =
      queryClient.getQueryData(counterpartiesDataQuery.queryKey) ??
      (await queryClient.fetchQuery(counterpartiesDataQuery));

    const permissionsListQuery = queryKeysClient[EQueryKeys.PermissionQueryKeys].getPermissionsList({
      offset: (DEFAULT_PAGE - 1) * DEFAULT_PAGE_SIZE,
      limit: DEFAULT_PAGE_SIZE,
    });
    const permissionsList: TGetPermissionsListResData =
      queryClient.getQueryData(permissionsListQuery.queryKey) ??
      (await queryClient.fetchQuery(permissionsListQuery));

    return {
      counterpartiesData,
      permissionsList,
    };
  } catch (error) {
    console.error(error);

    return {
      counterpartiesData: {},
      permissionsList: {},
    };
  }
};

export default UserRoleManagementPermissionsPage;
